// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * In-repo replacement for `semantic-ui-react`.
 *
 * Renders the same DOM (with the same `ui ...` CSS classes that
 * `semantic-ui-css` styles) but without `semantic-ui-react`'s runtime —
 * which calls the React-19-removed `ReactDOM.findDOMNode` via
 * `@fluentui/react-component-ref`.
 *
 * Only the props admin-ui actually uses are implemented. Component
 * signatures are kept compatible with the original `semantic-ui-react`
 * API so consumer files just change their import.
 */
import {
  Children,
  ChangeEvent,
  cloneElement,
  CSSProperties,
  forwardRef,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  TextareaHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AnyEvent = SyntheticEvent;
type CompositeColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'olive'
  | 'green'
  | 'teal'
  | 'blue'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'brown'
  | 'grey'
  | 'black';

type ClassToken = string | number | boolean | null | undefined | bigint;

const cx = (...tokens: ClassToken[]): string =>
  tokens.filter((t): t is string => typeof t === 'string' && t.length > 0).join(' ');

// ---------------------------------------------------------------------------
// Icon
// ---------------------------------------------------------------------------

export interface IconProps {
  name?: string;
  className?: string;
  color?: CompositeColor;
  size?: 'mini' | 'tiny' | 'small' | 'large' | 'big' | 'huge' | 'massive';
  link?: boolean;
  loading?: boolean;
  flipped?: 'horizontally' | 'vertically';
  rotated?: 'clockwise' | 'counterclockwise';
  fitted?: boolean;
  inverted?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
  title?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
}

export function Icon({
  name,
  className,
  color,
  size,
  link,
  loading,
  flipped,
  rotated,
  fitted,
  inverted,
  disabled,
  onClick,
  style,
  title,
  ...rest
}: IconProps) {
  return (
    <i
      aria-hidden="true"
      className={cx(
        color,
        size,
        link && 'link',
        loading && 'loading',
        fitted && 'fitted',
        inverted && 'inverted',
        disabled && 'disabled',
        flipped && `${flipped} flipped`,
        rotated && `${rotated} rotated`,
        name,
        'icon',
        className,
      )}
      style={style}
      title={title}
      onClick={onClick}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

export interface ContainerProps {
  text?: boolean;
  fluid?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Container({ text, fluid, className, style, children, ...rest }: ContainerProps) {
  return (
    <div
      className={cx('ui', text && 'text', fluid && 'fluid', 'container', className)}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Segment
// ---------------------------------------------------------------------------

export interface SegmentProps {
  basic?: boolean;
  raised?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  padded?: boolean | 'very';
  textAlign?: 'left' | 'right' | 'center' | 'justified';
  color?: CompositeColor;
  inverted?: boolean;
  loading?: boolean;
  placeholder?: boolean;
  compact?: boolean;
  attached?: boolean | 'top' | 'bottom';
  vertical?: boolean;
  clearing?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Segment({
  basic,
  raised,
  secondary,
  tertiary,
  padded,
  textAlign,
  color,
  inverted,
  loading,
  placeholder,
  compact,
  attached,
  vertical,
  clearing,
  className,
  style,
  children,
  ...rest
}: SegmentProps) {
  return (
    <div
      className={cx(
        'ui',
        color,
        basic && 'basic',
        raised && 'raised',
        secondary && 'secondary',
        tertiary && 'tertiary',
        padded === true && 'padded',
        padded === 'very' && 'very padded',
        textAlign && `${textAlign} aligned`,
        inverted && 'inverted',
        loading && 'loading',
        placeholder && 'placeholder',
        compact && 'compact',
        attached === true && 'attached',
        typeof attached === 'string' && `${attached} attached`,
        vertical && 'vertical',
        clearing && 'clearing',
        'segment',
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface HeaderProps {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
  icon?: string | boolean;
  content?: ReactNode;
  subheader?: ReactNode;
  textAlign?: 'left' | 'right' | 'center';
  dividing?: boolean;
  color?: CompositeColor;
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'huge';
  inverted?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Header({
  as: Tag = 'div',
  icon,
  content,
  subheader,
  textAlign,
  dividing,
  color,
  size,
  inverted,
  className,
  style,
  children,
  ...rest
}: HeaderProps) {
  const iconNode =
    typeof icon === 'string' ? <Icon name={icon} /> : null;
  const inferredIconClass = typeof icon !== 'undefined' && icon !== false ? 'icon' : null;

  return (
    <Tag
      className={cx(
        'ui',
        color,
        size,
        textAlign && `${textAlign} aligned`,
        dividing && 'dividing',
        inverted && 'inverted',
        inferredIconClass,
        'header',
        className,
      )}
      style={style}
      {...rest}
    >
      {iconNode}
      {content ?? children}
      {subheader ? <div className="sub header">{subheader}</div> : null}
    </Tag>
  );
}

interface HeaderSubheaderProps {
  className?: string;
  children?: ReactNode;
}

function HeaderSubheader({ className, children }: HeaderSubheaderProps) {
  return <div className={cx('sub header', className)}>{children}</div>;
}
Header.Subheader = HeaderSubheader;

interface HeaderContentProps {
  className?: string;
  children?: ReactNode;
}

function HeaderContent({ className, children }: HeaderContentProps) {
  return <div className={cx('content', className)}>{children}</div>;
}
Header.Content = HeaderContent;

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export interface ButtonProps {
  primary?: boolean;
  secondary?: boolean;
  positive?: boolean;
  negative?: boolean;
  basic?: boolean;
  fluid?: boolean;
  compact?: boolean;
  circular?: boolean;
  inverted?: boolean;
  color?: CompositeColor;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge';
  icon?: string | boolean | ReactNode;
  labelPosition?: 'left' | 'right';
  attached?: boolean | 'top' | 'bottom' | 'left' | 'right';
  floated?: 'left' | 'right';
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
  'data-testid'?: string;
  'aria-label'?: string;
  title?: string;
  as?: 'button' | 'a' | 'div';
  href?: string;
  target?: string;
  id?: string;
}

export function Button({
  primary,
  secondary,
  positive,
  negative,
  basic,
  fluid,
  compact,
  circular,
  inverted,
  color,
  size,
  icon,
  labelPosition,
  attached,
  floated,
  loading,
  disabled,
  active,
  className,
  style,
  onClick,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  const iconOnly =
    (typeof icon === 'string' || isValidElement(icon)) &&
    (children === undefined || children === null);
  const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        'ui',
        color,
        size,
        primary && 'primary',
        secondary && 'secondary',
        positive && 'positive',
        negative && 'negative',
        basic && 'basic',
        fluid && 'fluid',
        compact && 'compact',
        circular && 'circular',
        inverted && 'inverted',
        loading && 'loading',
        disabled && 'disabled',
        active && 'active',
        labelPosition && `${labelPosition} labeled`,
        attached === true && 'attached',
        typeof attached === 'string' && `${attached} attached`,
        floated && `${floated} floated`,
        iconOnly && 'icon',
        'button',
        className,
      )}
      style={style}
      {...rest}
    >
      {iconNode}
      {iconNode && children ? ' ' : null}
      {children}
    </button>
  );
}

interface ButtonGroupProps {
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge';
  basic?: boolean;
  vertical?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function ButtonGroup({ size, basic, vertical, className, style, children }: ButtonGroupProps) {
  return (
    <div
      className={cx(
        'ui',
        size,
        basic && 'basic',
        vertical && 'vertical',
        'buttons',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Button.Group = ButtonGroup;

function ButtonOr({ text = 'or', className }: { text?: string; className?: string }) {
  return <div className={cx('or', className)} data-text={text} />;
}
Button.Or = ButtonOr;

function ButtonContent({
  visible,
  hidden,
  className,
  children,
}: {
  visible?: boolean;
  hidden?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span className={cx(visible && 'visible content', hidden && 'hidden content', className)}>
      {children}
    </span>
  );
}
Button.Content = ButtonContent;

// ---------------------------------------------------------------------------
// Message
// ---------------------------------------------------------------------------

export interface MessageProps {
  positive?: boolean;
  negative?: boolean;
  warning?: boolean;
  info?: boolean;
  success?: boolean;
  error?: boolean;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'massive';
  icon?: string | boolean | ReactNode;
  hidden?: boolean;
  color?: CompositeColor;
  header?: ReactNode;
  content?: ReactNode;
  list?: ReactNode[];
  onDismiss?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Message({
  positive,
  negative,
  warning,
  info,
  success,
  error,
  size,
  icon,
  hidden,
  color,
  header,
  content,
  list,
  onDismiss,
  className,
  style,
  children,
  ...rest
}: MessageProps) {
  const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon;
  const hasIcon = Boolean(icon);

  const body = (
    <>
      {header ? <div className="header">{header}</div> : null}
      {list ? (
        <ul className="list">
          {list.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      ) : null}
      {content ? <p>{content}</p> : null}
      {children}
    </>
  );

  return (
    <div
      role="alert"
      className={cx(
        'ui',
        color,
        size,
        positive && 'positive',
        negative && 'negative',
        warning && 'warning',
        info && 'info',
        success && 'success',
        error && 'error',
        hidden && 'hidden',
        hasIcon && 'icon',
        'message',
        className,
      )}
      style={style}
      {...rest}
    >
      {onDismiss ? (
        <i
          className="close icon"
          role="button"
          aria-label="Dismiss"
          onClick={onDismiss as unknown as (e: MouseEvent<HTMLElement>) => void}
        />
      ) : null}
      {hasIcon ? (
        <>
          {iconNode}
          <div className="content">{body}</div>
        </>
      ) : (
        body
      )}
    </div>
  );
}

interface MessageHeaderProps {
  className?: string;
  children?: ReactNode;
}
function MessageHeader({ className, children }: MessageHeaderProps) {
  return <div className={cx('header', className)}>{children}</div>;
}
Message.Header = MessageHeader;

interface MessageContentProps {
  className?: string;
  children?: ReactNode;
}
function MessageContent({ className, children }: MessageContentProps) {
  return <div className={cx('content', className)}>{children}</div>;
}
Message.Content = MessageContent;

interface MessageListProps {
  className?: string;
  items?: Array<ReactNode | { key?: string | number; content: ReactNode }>;
  children?: ReactNode;
}
function MessageList({ className, items, children }: MessageListProps) {
  return (
    <ul className={cx('list', className)}>
      {items
        ? items.map((it, i) => {
            if (it && typeof it === 'object' && 'content' in (it as { content?: ReactNode })) {
              const entry = it as { key?: string | number; content: ReactNode };
              return <li key={entry.key ?? i}>{entry.content}</li>;
            }
            return <li key={i}>{it as ReactNode}</li>;
          })
        : children}
    </ul>
  );
}
Message.List = MessageList;

interface MessageItemProps {
  className?: string;
  children?: ReactNode;
}
function MessageItem({ className, children }: MessageItemProps) {
  return <li className={className}>{children}</li>;
}
Message.Item = MessageItem;

// ---------------------------------------------------------------------------
// Loader (and inline Dimmer)
// ---------------------------------------------------------------------------

export interface LoaderProps {
  active?: boolean;
  inline?: boolean | 'centered';
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  inverted?: boolean;
  indeterminate?: boolean;
  content?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

export function Loader({
  active = true,
  inline,
  size,
  inverted,
  indeterminate,
  content,
  className,
  style,
  children,
}: LoaderProps) {
  return (
    <div
      className={cx(
        'ui',
        size,
        active && 'active',
        inline === 'centered' && 'inline centered',
        inline === true && 'inline',
        inverted && 'inverted',
        indeterminate && 'indeterminate',
        'loader',
        className,
      )}
      style={style}
      role="status"
      aria-live="polite"
    >
      {content ?? children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Label
// ---------------------------------------------------------------------------

export interface LabelProps {
  color?: CompositeColor;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge';
  basic?: boolean;
  pointing?: boolean | 'above' | 'below' | 'left' | 'right';
  tag?: boolean;
  ribbon?: boolean | 'right';
  circular?: boolean;
  empty?: boolean;
  horizontal?: boolean;
  attached?: 'top' | 'bottom' | 'top right' | 'top left' | 'bottom right' | 'bottom left';
  icon?: string | ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  'data-testid'?: string;
}

export function Label({
  color,
  size,
  basic,
  pointing,
  tag,
  ribbon,
  circular,
  empty,
  horizontal,
  attached,
  icon,
  className,
  style,
  children,
  onClick,
  ...rest
}: LabelProps) {
  const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon;
  return (
    <span
      className={cx(
        'ui',
        color,
        size,
        basic && 'basic',
        tag && 'tag',
        ribbon === true && 'ribbon',
        ribbon === 'right' && 'right ribbon',
        circular && 'circular',
        empty && 'empty',
        horizontal && 'horizontal',
        attached && `${attached} attached`,
        pointing === true && 'pointing',
        typeof pointing === 'string' && `${pointing} pointing`,
        'label',
        className,
      )}
      style={style}
      onClick={onClick}
      {...rest}
    >
      {iconNode}
      {iconNode && children ? ' ' : null}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export interface InputProps {
  type?: string;
  value?: string | number;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  id?: string;
  icon?: string | ReactNode;
  iconPosition?: 'left';
  fluid?: boolean;
  loading?: boolean;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge';
  label?: ReactNode;
  labelPosition?: 'left' | 'right' | 'left corner' | 'right corner';
  action?: ReactNode | boolean;
  actionPosition?: 'left';
  className?: string;
  style?: CSSProperties;
  onChange?: (event: ChangeEvent<HTMLInputElement>, data: { value: string }) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (event: SyntheticEvent<HTMLInputElement>) => void;
  onFocus?: (event: SyntheticEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  autoComplete?: string;
  required?: boolean;
  pattern?: string;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  maxLength?: number;
  minLength?: number;
  accept?: string;
  multiple?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
  title?: string;
}

export function Input({
  type = 'text',
  value,
  placeholder,
  disabled,
  readOnly,
  name,
  id,
  icon,
  iconPosition,
  fluid,
  loading,
  size,
  label,
  labelPosition,
  action,
  actionPosition,
  className,
  style,
  onChange,
  onKeyPress,
  onKeyDown,
  onKeyUp,
  onBlur,
  onFocus,
  onClick,
  autoFocus,
  autoComplete,
  required,
  pattern,
  min,
  max,
  step,
  maxLength,
  minLength,
  accept,
  multiple,
  ...rest
}: InputProps) {
  const inputEl = (
    <input
      type={type}
      id={id}
      value={value as string | number}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      name={name}
      required={required}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      pattern={pattern}
      min={min}
      max={max}
      step={step}
      maxLength={maxLength}
      minLength={minLength}
      accept={accept}
      multiple={multiple}
      onChange={(e) => onChange?.(e, { value: e.target.value })}
      onKeyPress={onKeyPress}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={onBlur}
      onFocus={onFocus}
      onClick={onClick}
      {...rest}
    />
  );

  const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon;

  const wrapperClass = cx(
    'ui',
    size,
    fluid && 'fluid',
    loading && 'loading',
    iconNode && (iconPosition === 'left' ? 'left icon' : 'icon'),
    label && labelPosition && `${labelPosition} labeled`,
    action && (actionPosition === 'left' ? 'left action' : 'action'),
    'input',
    className,
  );

  return (
    <div className={wrapperClass} style={style}>
      {action && actionPosition === 'left' ? action : null}
      {label && labelPosition?.startsWith('left') ? (
        <div className="ui label">{label}</div>
      ) : null}
      {inputEl}
      {iconNode}
      {action && actionPosition !== 'left' ? action : null}
      {label && (labelPosition === 'right' || labelPosition === 'right corner') ? (
        <div className="ui label">{label}</div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextArea
// ---------------------------------------------------------------------------

export interface TextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value?: string;
  onChange?: (
    event: ChangeEvent<HTMLTextAreaElement>,
    data: { value: string; name?: string },
  ) => void;
  rows?: number;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { value, onChange, rows = 3, className, style, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      value={value}
      rows={rows}
      className={className}
      style={style}
      onChange={(e) => onChange?.(e, { value: e.target.value, name: e.target.name })}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// Dropdown
// ---------------------------------------------------------------------------

export interface DropdownItemProps {
  key?: string | number;
  text: ReactNode;
  value: string | number | boolean | undefined;
  disabled?: boolean;
  description?: ReactNode;
  icon?: string;
  image?: { avatar?: boolean; src: string };
  label?: ReactNode;
  selected?: boolean;
  active?: boolean;
}

export interface DropdownProps {
  options?: DropdownItemProps[];
  value?:
    | string
    | number
    | boolean
    | Array<string | number | boolean>
    | undefined;
  defaultValue?:
    | string
    | number
    | boolean
    | Array<string | number | boolean>
    | undefined;
  placeholder?: string;
  selection?: boolean;
  search?: boolean | ((options: DropdownItemProps[], query: string) => DropdownItemProps[]);
  multiple?: boolean;
  compact?: boolean;
  fluid?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  inline?: boolean;
  upward?: boolean;
  floating?: boolean;
  button?: boolean;
  direction?: 'left' | 'right';
  scrolling?: boolean;
  pointing?: boolean | 'top' | 'top left' | 'top right' | 'bottom' | 'bottom left' | 'bottom right';
  icon?: string | ReactNode;
  text?: ReactNode;
  trigger?: ReactNode;
  closeOnEscape?: boolean;
  className?: string;
  style?: CSSProperties;
  name?: string;
  id?: string;
  children?: ReactNode;
  onChange?: (
    event: SyntheticEvent<HTMLElement>,
    data: {
      value: string | number | boolean | Array<string | number | boolean> | undefined;
      name?: string;
    },
  ) => void;
  onOpen?: (event: SyntheticEvent<HTMLElement>) => void;
  onClose?: (event: SyntheticEvent<HTMLElement>) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

interface DropdownComponent extends React.FC<DropdownProps> {
  Menu: typeof DropdownMenu;
  Item: typeof DropdownItem;
  Divider: typeof DropdownDivider;
  Header: typeof DropdownHeader;
}

function DropdownBase({
  options,
  value,
  defaultValue,
  placeholder,
  selection,
  search,
  multiple,
  compact,
  fluid,
  clearable,
  disabled,
  loading,
  inline,
  upward,
  floating,
  button,
  direction,
  scrolling,
  pointing,
  icon = 'dropdown',
  text,
  trigger,
  closeOnEscape = true,
  className,
  style,
  name,
  children,
  onChange,
  onOpen,
  onClose,
  ...rest
}: DropdownProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<DropdownProps['value']>(
    defaultValue ?? (multiple ? [] : ''),
  );
  const currentValue = isControlled ? value : internalValue;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (
    event: SyntheticEvent<HTMLElement>,
    optionValue: DropdownItemProps['value'],
  ) => {
    if (optionValue === undefined) return;
    if (multiple) {
      const current = (Array.isArray(currentValue) ? currentValue : []) as Array<
        string | number | boolean
      >;
      const next = current.includes(optionValue)
        ? current.filter((v) => v !== optionValue)
        : ([...current, optionValue] as Array<string | number | boolean>);
      if (!isControlled) setInternalValue(next);
      onChange?.(event, { value: next, name });
    } else {
      if (!isControlled) setInternalValue(optionValue);
      onChange?.(event, { value: optionValue, name });
      setOpen(false);
      setSearchQuery('');
    }
  };

  const filteredOptions = useMemo(() => {
    if (!options) return undefined;
    if (!searchQuery) return options;
    const lower = searchQuery.toLowerCase();
    return options.filter((opt) => String(opt.text).toLowerCase().includes(lower));
  }, [options, searchQuery]);

  const selectedOption = useMemo(() => {
    if (!options || multiple || currentValue === undefined || currentValue === '') return undefined;
    return options.find((opt) => opt.value === currentValue);
  }, [options, currentValue, multiple]);

  const renderTrigger = () => {
    if (trigger) return trigger;
    if (text) return <div className="text">{text}</div>;
    if (selectedOption) return <div className="text">{selectedOption.text}</div>;
    if (multiple && Array.isArray(currentValue) && currentValue.length > 0) {
      return (
        <div className="text">
          {currentValue
            .map((v) => options?.find((o) => o.value === v)?.text ?? String(v))
            .join(', ')}
        </div>
      );
    }
    return <div className="default text">{placeholder ?? ''}</div>;
  };

  return (
    <div
      ref={rootRef}
      role="listbox"
      className={cx(
        'ui',
        selection && 'selection',
        search && 'search',
        multiple && 'multiple',
        compact && 'compact',
        fluid && 'fluid',
        clearable && 'clearable',
        disabled && 'disabled',
        loading && 'loading',
        inline && 'inline',
        upward && 'upward',
        floating && 'floating',
        button && 'button',
        scrolling && 'scrolling',
        direction === 'left' && 'left',
        pointing === true && 'pointing',
        typeof pointing === 'string' && `${pointing} pointing`,
        open && 'active visible',
        'dropdown',
        className,
      )}
      style={style}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && setOpen((o) => !o)}
      onKeyDown={(e) => {
        if (closeOnEscape && e.key === 'Escape') setOpen(false);
        if (e.key === 'Enter' || e.key === ' ') setOpen((o) => !o);
      }}
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node)) {
          setOpen(false);
          onClose?.(e);
        }
      }}
      onFocus={(e) => onOpen?.(e)}
      {...rest}
    >
      {renderTrigger()}
      {typeof icon === 'string' ? <Icon name={icon} /> : icon}
      {search ? (
        <input
          className="search"
          type="text"
          value={searchQuery}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
          tabIndex={open ? 0 : -1}
        />
      ) : null}
      <div
        className={cx('menu', open ? 'visible transition' : 'transition hidden')}
        style={open ? undefined : { display: 'none' }}
      >
        {filteredOptions?.map((opt, i) => {
          const isSelected = multiple
            ? Array.isArray(currentValue) &&
              opt.value !== undefined &&
              (currentValue as Array<string | number | boolean>).includes(
                opt.value as string | number | boolean,
              )
            : currentValue === opt.value;
          return (
            <div
              role="option"
              aria-selected={isSelected}
              key={opt.key ?? String(opt.value) ?? i}
              className={cx('item', isSelected && 'selected active', opt.disabled && 'disabled')}
              onClick={(e) => {
                e.stopPropagation();
                if (!opt.disabled) handleSelect(e, opt.value);
              }}
            >
              {opt.icon ? <Icon name={opt.icon} /> : null}
              {opt.image ? (
                <img
                  src={opt.image.src}
                  className={opt.image.avatar ? 'ui avatar image' : 'ui image'}
                  alt=""
                />
              ) : null}
              <span className="text">{opt.text}</span>
              {opt.description ? <span className="description">{opt.description}</span> : null}
            </div>
          );
        })}
        {children}
      </div>
    </div>
  );
}

const Dropdown = DropdownBase as DropdownComponent;

interface DropdownMenuProps {
  className?: string;
  children?: ReactNode;
}
function DropdownMenu({ className, children }: DropdownMenuProps) {
  return <div className={cx('menu', className)}>{children}</div>;
}
Dropdown.Menu = DropdownMenu;

interface DropdownItemUIProps {
  text?: ReactNode;
  value?: string | number | boolean | undefined;
  description?: ReactNode;
  icon?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  selected?: boolean;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  label?: ReactNode;
  children?: ReactNode;
}
function DropdownItem({
  text,
  description,
  icon,
  onClick,
  selected,
  active,
  disabled,
  className,
  style,
  children,
}: DropdownItemUIProps) {
  return (
    <div
      role="option"
      aria-selected={selected || active}
      onClick={onClick}
      style={style}
      className={cx(
        'item',
        (selected || active) && 'selected active',
        disabled && 'disabled',
        className,
      )}
    >
      {icon ? <Icon name={icon} /> : null}
      {text != null ? <span className="text">{text}</span> : children}
      {description ? <span className="description">{description}</span> : null}
    </div>
  );
}
Dropdown.Item = DropdownItem;

function DropdownDivider() {
  return <div className="divider" />;
}
Dropdown.Divider = DropdownDivider;

function DropdownHeader({ children }: { children?: ReactNode }) {
  return <div className="header">{children}</div>;
}
Dropdown.Header = DropdownHeader;

export { Dropdown };

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export interface FormProps {
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  loading?: boolean;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  error?: boolean;
  success?: boolean;
  warning?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  noValidate?: boolean;
  autoComplete?: string;
}

interface FormComponent extends React.FC<FormProps> {
  Field: typeof FormField;
  Input: typeof FormInputField;
  Select: typeof FormSelectField;
  TextArea: typeof FormTextAreaField;
  Checkbox: typeof FormCheckboxField;
  Radio: typeof FormRadioField;
  Group: typeof FormGroup;
}

function FormBase({
  onSubmit,
  loading,
  size,
  error,
  success,
  warning,
  className,
  style,
  children,
  noValidate,
  autoComplete,
  ...rest
}: FormProps) {
  return (
    <form
      onSubmit={onSubmit}
      noValidate={noValidate}
      autoComplete={autoComplete}
      className={cx(
        'ui',
        size,
        loading && 'loading',
        error && 'error',
        success && 'success',
        warning && 'warning',
        'form',
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </form>
  );
}

const Form = FormBase as FormComponent;

export interface FormFieldProps {
  required?: boolean;
  error?: boolean | { content: ReactNode };
  disabled?: boolean;
  width?: number | 'equal';
  inline?: boolean;
  label?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

function FormField({
  required,
  error,
  disabled,
  width,
  inline,
  label,
  className,
  style,
  children,
}: FormFieldProps) {
  const errorObj = typeof error === 'object' ? error : null;
  return (
    <div
      className={cx(
        typeof width === 'number' && `${numberToWord(width)} wide`,
        width === 'equal' && 'equal width',
        required && 'required',
        Boolean(error) && 'error',
        disabled && 'disabled',
        inline && 'inline',
        'field',
        className,
      )}
      style={style}
    >
      {label ? <label>{label}</label> : null}
      {children}
      {errorObj ? (
        <div className="ui pointing red basic label" role="alert">
          {errorObj.content}
        </div>
      ) : null}
    </div>
  );
}
Form.Field = FormField;

const numberToWord = (n: number) =>
  [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
  ][n] ?? '';

export interface FormInputFieldProps extends Omit<InputProps, 'label'> {
  label?: ReactNode;
  required?: boolean;
  error?: boolean | { content: ReactNode };
  width?: number | 'equal';
  inline?: boolean;
  id?: string;
}

function FormInputField({
  label,
  required,
  error,
  width,
  inline,
  className: fieldClassName,
  ...inputProps
}: FormInputFieldProps) {
  return (
    <FormField
      required={required}
      error={error}
      width={width}
      inline={inline}
      label={label}
      className={fieldClassName}
    >
      <Input {...inputProps} />
    </FormField>
  );
}
Form.Input = FormInputField;

export interface FormSelectFieldProps {
  label?: ReactNode;
  required?: boolean;
  error?: boolean | { content: ReactNode };
  width?: number | 'equal';
  inline?: boolean;
  options?: DropdownItemProps[];
  value?: DropdownProps['value'];
  placeholder?: string;
  disabled?: boolean;
  search?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  loading?: boolean;
  className?: string;
  onChange?: DropdownProps['onChange'];
}

function FormSelectField({
  label,
  required,
  error,
  width,
  inline,
  options,
  value,
  placeholder,
  disabled,
  search,
  multiple,
  clearable,
  loading,
  className,
  onChange,
}: FormSelectFieldProps) {
  return (
    <FormField required={required} error={error} width={width} inline={inline} label={label} className={className}>
      <Dropdown
        selection
        options={options}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        search={search}
        multiple={multiple}
        clearable={clearable}
        loading={loading}
        onChange={onChange}
      />
    </FormField>
  );
}
Form.Select = FormSelectField;

export interface FormTextAreaFieldProps extends TextAreaProps {
  label?: ReactNode;
  required?: boolean;
  error?: boolean | { content: ReactNode };
  width?: number | 'equal';
  inline?: boolean;
}

function FormTextAreaField({
  label,
  required,
  error,
  width,
  inline,
  className,
  ...textAreaProps
}: FormTextAreaFieldProps) {
  return (
    <FormField required={required} error={error} width={width} inline={inline} label={label} className={className}>
      <TextArea {...textAreaProps} />
    </FormField>
  );
}
Form.TextArea = FormTextAreaField;

export interface FormCheckboxFieldProps {
  label?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  toggle?: boolean;
  slider?: boolean;
  name?: string;
  className?: string;
  style?: CSSProperties;
  onChange?: (
    event: React.FormEvent<HTMLInputElement>,
    data: { checked: boolean; name?: string },
  ) => void;
}

function FormCheckboxField({
  label,
  checked,
  disabled,
  toggle,
  slider,
  name,
  className,
  style,
  onChange,
}: FormCheckboxFieldProps) {
  return (
    <FormField className={cx('field', className)} style={style}>
      <div
        className={cx(
          'ui',
          toggle && 'toggle',
          slider && 'slider',
          checked && 'checked',
          disabled && 'disabled',
          'checkbox',
        )}
      >
        <input
          type="checkbox"
          name={name}
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(e) => onChange?.(e, { checked: e.target.checked, name })}
        />
        <label>{label}</label>
      </div>
    </FormField>
  );
}
Form.Checkbox = FormCheckboxField;

function FormRadioField({
  label,
  checked,
  disabled,
  name,
  value,
  className,
  onChange,
}: {
  label?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  name?: string;
  value?: string | number;
  className?: string;
  onChange?: (
    event: React.FormEvent<HTMLInputElement>,
    data: { checked: boolean; value?: string | number; name?: string },
  ) => void;
}) {
  return (
    <FormField className={cx('field', className)}>
      <div className={cx('ui radio checkbox', checked && 'checked', disabled && 'disabled')}>
        <input
          type="radio"
          name={name}
          checked={Boolean(checked)}
          disabled={disabled}
          value={value}
          onChange={(e) => onChange?.(e, { checked: e.target.checked, value, name })}
        />
        <label>{label}</label>
      </div>
    </FormField>
  );
}
Form.Radio = FormRadioField;

interface FormGroupProps {
  widths?: 'equal' | number;
  grouped?: boolean;
  inline?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function FormGroup({ widths, grouped, inline, className, style, children }: FormGroupProps) {
  // semantic-ui-react's Form.Group widths='equal' makes children equal width by
  // adding the "equal width" modifier on the wrapping fields div. To preserve
  // the same behavior with arbitrary Field children, we apply width='equal' to
  // each child Form.Field if widths is 'equal'.
  const enhancedChildren =
    widths === 'equal'
      ? Children.map(children, (child) => {
          if (isValidElement<FormFieldProps>(child)) {
            return cloneElement<FormFieldProps>(child, {
              width: child.props.width ?? 'equal',
            });
          }
          return child;
        })
      : children;
  return (
    <div
      className={cx(
        widths === 'equal' && 'equal width',
        typeof widths === 'number' && `${numberToWord(widths)}`,
        grouped && 'grouped',
        inline && 'inline',
        'fields',
        className,
      )}
      style={style}
    >
      {enhancedChildren}
    </div>
  );
}
Form.Group = FormGroup;

export { Form };

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export interface TableProps {
  celled?: boolean;
  basic?: boolean | 'very';
  compact?: boolean | 'very';
  collapsing?: boolean;
  selectable?: boolean;
  striped?: boolean;
  stackable?: boolean;
  unstackable?: boolean;
  fixed?: boolean;
  singleLine?: boolean;
  padded?: boolean | 'very';
  size?: 'small' | 'large';
  color?: CompositeColor;
  inverted?: boolean;
  sortable?: boolean;
  attached?: boolean | 'top' | 'bottom';
  definition?: boolean;
  structured?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

interface TableComponent extends React.FC<TableProps> {
  Header: typeof TableHeader;
  Body: typeof TableBody;
  Row: typeof TableRow;
  Cell: typeof TableCell;
  HeaderCell: typeof TableHeaderCell;
  Footer: typeof TableFooter;
}

function TableBase({
  celled,
  basic,
  compact,
  collapsing,
  selectable,
  striped,
  stackable,
  unstackable,
  fixed,
  singleLine,
  padded,
  size,
  color,
  inverted,
  sortable,
  attached,
  definition,
  structured,
  className,
  style,
  children,
  ...rest
}: TableProps) {
  return (
    <table
      className={cx(
        'ui',
        color,
        size,
        celled && 'celled',
        basic === true && 'basic',
        basic === 'very' && 'very basic',
        compact === true && 'compact',
        compact === 'very' && 'very compact',
        padded === true && 'padded',
        padded === 'very' && 'very padded',
        collapsing && 'collapsing',
        selectable && 'selectable',
        striped && 'striped',
        stackable && 'stackable',
        unstackable && 'unstackable',
        fixed && 'fixed',
        singleLine && 'single line',
        inverted && 'inverted',
        sortable && 'sortable',
        attached === true && 'attached',
        attached === 'top' && 'top attached',
        attached === 'bottom' && 'bottom attached',
        definition && 'definition',
        structured && 'structured',
        'table',
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </table>
  );
}

const Table = TableBase as TableComponent;

interface TableHeaderProps {
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}
function TableHeader({ fullWidth, className, children }: TableHeaderProps) {
  return <thead className={cx(fullWidth && 'full-width', className)}>{children}</thead>;
}
Table.Header = TableHeader;

interface TableBodyProps {
  className?: string;
  children?: ReactNode;
}
function TableBody({ className, children }: TableBodyProps) {
  return <tbody className={className}>{children}</tbody>;
}
Table.Body = TableBody;

interface TableFooterProps {
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
}
function TableFooter({ fullWidth, className, children }: TableFooterProps) {
  return <tfoot className={cx(fullWidth && 'full-width', className)}>{children}</tfoot>;
}
Table.Footer = TableFooter;

interface TableRowProps {
  active?: boolean;
  disabled?: boolean;
  negative?: boolean;
  positive?: boolean;
  warning?: boolean;
  error?: boolean;
  textAlign?: 'left' | 'right' | 'center';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  className?: string;
  style?: CSSProperties;
  onClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
  children?: ReactNode;
}
function TableRow({
  active,
  disabled,
  negative,
  positive,
  warning,
  error,
  textAlign,
  verticalAlign,
  className,
  style,
  onClick,
  children,
}: TableRowProps) {
  return (
    <tr
      onClick={onClick}
      className={cx(
        active && 'active',
        disabled && 'disabled',
        negative && 'negative',
        positive && 'positive',
        warning && 'warning',
        error && 'error',
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        className,
      )}
      style={style}
    >
      {children}
    </tr>
  );
}
Table.Row = TableRow;

interface TableCellProps {
  textAlign?: 'left' | 'right' | 'center';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  collapsing?: boolean;
  singleLine?: boolean;
  width?: number;
  colSpan?: number | string;
  rowSpan?: number | string;
  selectable?: boolean;
  negative?: boolean;
  positive?: boolean;
  warning?: boolean;
  error?: boolean;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function TableCell({
  textAlign,
  verticalAlign,
  collapsing,
  singleLine,
  width,
  colSpan,
  rowSpan,
  selectable,
  negative,
  positive,
  warning,
  error,
  active,
  disabled,
  className,
  style,
  children,
}: TableCellProps) {
  const colSpanNum = typeof colSpan === 'string' ? Number(colSpan) : colSpan;
  const rowSpanNum = typeof rowSpan === 'string' ? Number(rowSpan) : rowSpan;
  return (
    <td
      colSpan={colSpanNum}
      rowSpan={rowSpanNum}
      className={cx(
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        collapsing && 'collapsing',
        singleLine && 'single line',
        width != null && `${numberToWord(width)} wide`,
        selectable && 'selectable',
        negative && 'negative',
        positive && 'positive',
        warning && 'warning',
        error && 'error',
        active && 'active',
        disabled && 'disabled',
        className,
      )}
      style={style}
    >
      {children}
    </td>
  );
}
Table.Cell = TableCell;

interface TableHeaderCellProps extends Omit<TableCellProps, 'children'> {
  sorted?: 'ascending' | 'descending';
  onClick?: (event: MouseEvent<HTMLTableCellElement>) => void;
  children?: ReactNode;
}
function TableHeaderCell({
  sorted,
  textAlign,
  collapsing,
  singleLine,
  width,
  colSpan,
  rowSpan,
  className,
  style,
  onClick,
  children,
}: TableHeaderCellProps) {
  const colSpanNum = typeof colSpan === 'string' ? Number(colSpan) : colSpan;
  const rowSpanNum = typeof rowSpan === 'string' ? Number(rowSpan) : rowSpan;
  return (
    <th
      colSpan={colSpanNum}
      rowSpan={rowSpanNum}
      onClick={onClick}
      className={cx(
        sorted && `${sorted} sorted`,
        textAlign && `${textAlign} aligned`,
        collapsing && 'collapsing',
        singleLine && 'single line',
        width != null && `${numberToWord(width)} wide`,
        className,
      )}
      style={style}
    >
      {children}
    </th>
  );
}
Table.HeaderCell = TableHeaderCell;

export { Table };

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

type PaginationItem = ReactNode | { content: ReactNode; icon?: boolean } | null;

export interface PaginationProps {
  activePage: number;
  totalPages: number;
  onPageChange?: (
    event: MouseEvent<HTMLAnchorElement>,
    data: PaginationProps & { activePage: number; totalPages: number },
  ) => void;
  siblingRange?: number;
  boundaryRange?: number;
  ellipsisItem?: PaginationItem;
  firstItem?: PaginationItem;
  lastItem?: PaginationItem;
  prevItem?: PaginationItem;
  nextItem?: PaginationItem;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'huge';
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

const renderItem = (item: PaginationItem): ReactNode => {
  if (item == null) return null;
  if (
    typeof item === 'object' &&
    !isValidElement(item) &&
    'content' in (item as { content?: ReactNode })
  ) {
    return (item as { content: ReactNode }).content;
  }
  return item as ReactNode;
};

export function Pagination({
  activePage,
  totalPages,
  onPageChange,
  siblingRange = 1,
  boundaryRange = 1,
  ellipsisItem,
  firstItem,
  lastItem,
  prevItem,
  nextItem,
  size,
  className,
  style,
  disabled,
}: PaginationProps) {
  const pages = useMemo(() => {
    if (totalPages <= 0) return [] as Array<number | 'ellipsis'>;
    const result: Array<number | 'ellipsis'> = [];
    const left = activePage - siblingRange;
    const right = activePage + siblingRange;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i <= boundaryRange ||
        i > totalPages - boundaryRange ||
        (i >= left && i <= right)
      ) {
        result.push(i);
      } else if (result[result.length - 1] !== 'ellipsis') {
        result.push('ellipsis');
      }
    }
    return result;
  }, [activePage, totalPages, siblingRange, boundaryRange]);

  const goTo = (event: SyntheticEvent<HTMLElement>, page: number) => {
    if (disabled || page < 1 || page > totalPages || page === activePage) return;
    onPageChange?.(event as unknown as MouseEvent<HTMLAnchorElement>, {
      activePage: page,
      totalPages,
      onPageChange,
      siblingRange,
      boundaryRange,
      ellipsisItem,
      firstItem,
      lastItem,
      prevItem,
      nextItem,
      size,
      className,
      style,
      disabled,
    });
  };

  return (
    <div
      role="navigation"
      className={cx('ui', size, disabled && 'disabled', 'pagination menu', className)}
      style={style}
    >
      {firstItem !== null && firstItem !== undefined ? (
        <button
          type="button"
          className="item"
          onClick={(e) => goTo(e, 1)}
          disabled={disabled || activePage === 1}
        >
          {renderItem(firstItem) ?? '«'}
        </button>
      ) : null}
      {prevItem !== null && prevItem !== undefined ? (
        <button
          type="button"
          className="item"
          aria-label="Previous"
          onClick={(e) => goTo(e, activePage - 1)}
          disabled={disabled || activePage === 1}
        >
          {renderItem(prevItem) ?? '‹'}
        </button>
      ) : null}
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e-${i}`} className="disabled item">
            {ellipsisItem === null ? null : renderItem(ellipsisItem) ?? '…'}
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={cx('item', p === activePage && 'active')}
            aria-current={p === activePage ? 'page' : undefined}
            onClick={(e) => goTo(e, p)}
            disabled={disabled}
          >
            {p}
          </button>
        ),
      )}
      {nextItem !== null && nextItem !== undefined ? (
        <button
          type="button"
          className="item"
          aria-label="Next"
          onClick={(e) => goTo(e, activePage + 1)}
          disabled={disabled || activePage === totalPages}
        >
          {renderItem(nextItem) ?? '›'}
        </button>
      ) : null}
      {lastItem !== null && lastItem !== undefined ? (
        <button
          type="button"
          className="item"
          onClick={(e) => goTo(e, totalPages)}
          disabled={disabled || activePage === totalPages}
        >
          {renderItem(lastItem) ?? '»'}
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export interface ModalProps {
  open?: boolean;
  onOpen?: (event: AnyEvent) => void;
  onClose?: (event: AnyEvent) => void;
  size?: 'mini' | 'tiny' | 'small' | 'large' | 'fullscreen';
  closeOnDimmerClick?: boolean;
  closeOnEscape?: boolean;
  closeIcon?: boolean;
  basic?: boolean;
  centered?: boolean;
  dimmer?: boolean | 'blurring' | 'inverted';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  trigger?: ReactNode;
}

interface ModalComponent extends React.FC<ModalProps> {
  Header: typeof ModalHeader;
  Content: typeof ModalContent;
  Actions: typeof ModalActions;
  Description: typeof ModalDescription;
}

function ModalBase({
  open,
  onClose,
  size,
  closeOnDimmerClick = true,
  closeOnEscape = true,
  closeIcon,
  basic,
  centered = true,
  dimmer = true,
  className,
  style,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.(e as unknown as AnyEvent);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  return (
    <div
      className={cx('ui', dimmer && 'dimmer modals', dimmer === 'blurring' && 'blurring', 'page modals visible active')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 1000,
        display: 'flex',
        alignItems: centered ? 'center' : 'flex-start',
        justifyContent: 'center',
        padding: '2rem',
      }}
      onClick={(e) => {
        if (closeOnDimmerClick && e.target === e.currentTarget) {
          onClose?.(e);
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cx('ui', size, basic && 'basic', 'modal visible active', className)}
        style={{
          background: 'white',
          borderRadius: 4,
          boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
          maxWidth: '600px',
          width: '100%',
          ...style,
        }}
      >
        {closeIcon ? (
          <i
            className="close icon"
            role="button"
            aria-label="Close"
            onClick={(e) => onClose?.(e)}
          />
        ) : null}
        {children}
      </div>
    </div>
  );
}

const Modal = ModalBase as ModalComponent;

interface ModalHeaderProps {
  className?: string;
  children?: ReactNode;
}
function ModalHeader({ className, children }: ModalHeaderProps) {
  return <div className={cx('header', className)}>{children}</div>;
}
Modal.Header = ModalHeader;

interface ModalContentProps {
  image?: boolean;
  scrolling?: boolean;
  className?: string;
  children?: ReactNode;
}
function ModalContent({ image, scrolling, className, children }: ModalContentProps) {
  return (
    <div className={cx(image && 'image', scrolling && 'scrolling', 'content', className)}>
      {children}
    </div>
  );
}
Modal.Content = ModalContent;

interface ModalActionsProps {
  className?: string;
  children?: ReactNode;
}
function ModalActions({ className, children }: ModalActionsProps) {
  return <div className={cx('actions', className)}>{children}</div>;
}
Modal.Actions = ModalActions;

interface ModalDescriptionProps {
  className?: string;
  children?: ReactNode;
}
function ModalDescription({ className, children }: ModalDescriptionProps) {
  return <div className={cx('description', className)}>{children}</div>;
}
Modal.Description = ModalDescription;

export { Modal };

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export interface TabPaneShape {
  menuItem:
    | ReactNode
    | {
        key?: string | number;
        icon?: string;
        content?: ReactNode;
        disabled?: boolean;
        active?: boolean;
      };
  render?: () => ReactNode;
  pane?: ReactNode;
}

const renderMenuItem = (menuItem: TabPaneShape['menuItem']): ReactNode => {
  if (menuItem == null) return null;
  if (
    typeof menuItem === 'object' &&
    !isValidElement(menuItem) &&
    ('content' in menuItem || 'icon' in menuItem)
  ) {
    const obj = menuItem as { icon?: string; content?: ReactNode };
    return (
      <>
        {obj.icon ? <Icon name={obj.icon} /> : null}
        {obj.content}
      </>
    );
  }
  return menuItem as ReactNode;
};

export interface TabProps {
  panes: TabPaneShape[];
  activeIndex?: number;
  defaultActiveIndex?: number;
  onTabChange?: (event: SyntheticEvent<HTMLElement>, data: { activeIndex: number }) => void;
  menu?: { secondary?: boolean; pointing?: boolean; attached?: string; tabular?: boolean };
  className?: string;
  style?: CSSProperties;
}

interface TabComponent extends React.FC<TabProps> {
  Pane: typeof TabPane;
}

function TabBase({
  panes,
  activeIndex,
  defaultActiveIndex = 0,
  onTabChange,
  menu,
  className,
  style,
}: TabProps) {
  const isControlled = activeIndex !== undefined;
  const [internalIndex, setInternalIndex] = useState(defaultActiveIndex);
  const current = isControlled ? activeIndex : internalIndex;

  const handleClick = (event: SyntheticEvent<HTMLElement>, index: number) => {
    if (!isControlled) setInternalIndex(index);
    onTabChange?.(event, { activeIndex: index });
  };

  const activePane = panes[current];
  const paneContent = activePane?.render ? activePane.render() : activePane?.pane;

  return (
    <div className={cx('ui', menu?.tabular && 'top attached tabular menu', className)} style={style}>
      <div className={cx('ui', menu?.secondary && 'secondary', menu?.pointing && 'pointing', 'menu')}>
        {panes.map((pane, i) => (
          <button
            type="button"
            key={i}
            role="tab"
            aria-selected={i === current}
            className={cx('item', i === current && 'active')}
            onClick={(e) => handleClick(e, i)}
          >
            {renderMenuItem(pane.menuItem)}
          </button>
        ))}
      </div>
      <div role="tabpanel">{paneContent}</div>
    </div>
  );
}

const Tab = TabBase as TabComponent;

interface TabPaneProps {
  attached?: boolean | 'top' | 'bottom';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function TabPane({ attached, className, style, children }: TabPaneProps) {
  return (
    <div
      className={cx(
        'ui',
        attached === 'top' && 'top attached',
        attached === 'bottom' && 'bottom attached',
        attached === true && 'attached',
        'tab segment',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Tab.Pane = TabPane;

export { Tab };

// ---------------------------------------------------------------------------
// Statistic (single + group)
// ---------------------------------------------------------------------------

export interface StatisticProps {
  size?: 'mini' | 'tiny' | 'small' | 'large' | 'huge';
  color?: CompositeColor;
  horizontal?: boolean;
  inverted?: boolean;
  label?: ReactNode;
  value?: ReactNode;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

interface StatisticComponent extends React.FC<StatisticProps> {
  Group: typeof StatisticGroup;
  Value: typeof StatisticValue;
  Label: typeof StatisticLabel;
}

function StatisticBase({
  size,
  color,
  horizontal,
  inverted,
  label,
  value,
  className,
  style,
  children,
}: StatisticProps) {
  return (
    <div
      className={cx(
        'ui',
        color,
        size,
        horizontal && 'horizontal',
        inverted && 'inverted',
        'statistic',
        className,
      )}
      style={style}
    >
      {value != null ? <div className="value">{value}</div> : null}
      {label != null ? <div className="label">{label}</div> : null}
      {children}
    </div>
  );
}

const Statistic = StatisticBase as StatisticComponent;

interface StatisticGroupProps {
  size?: StatisticProps['size'];
  widths?: number | string;
  horizontal?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function StatisticGroup({ size, widths, horizontal, className, style, children }: StatisticGroupProps) {
  return (
    <div
      className={cx(
        'ui',
        size,
        horizontal && 'horizontal',
        typeof widths === 'number' && numberToWord(widths),
        typeof widths === 'string' && widths,
        'statistics',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Statistic.Group = StatisticGroup;

function StatisticValue({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('value', className)}>{children}</div>;
}
Statistic.Value = StatisticValue;

function StatisticLabel({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('label', className)}>{children}</div>;
}
Statistic.Label = StatisticLabel;

export { Statistic };

// ---------------------------------------------------------------------------
// Grid (lightweight)
// ---------------------------------------------------------------------------

export interface GridProps {
  columns?: number | 'equal';
  divided?: boolean | 'vertically';
  padded?: boolean | 'horizontally' | 'vertically';
  stackable?: boolean;
  centered?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

interface GridComponent extends React.FC<GridProps> {
  Row: typeof GridRow;
  Column: typeof GridColumn;
}

function GridBase({ columns, divided, padded, stackable, centered, className, style, children }: GridProps) {
  return (
    <div
      className={cx(
        'ui',
        columns === 'equal' && 'equal width',
        typeof columns === 'number' && `${numberToWord(columns)} column`,
        divided === true && 'divided',
        divided === 'vertically' && 'vertically divided',
        padded === true && 'padded',
        padded === 'horizontally' && 'horizontally padded',
        padded === 'vertically' && 'vertically padded',
        stackable && 'stackable',
        centered && 'centered',
        'grid',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

const Grid = GridBase as GridComponent;

interface GridRowProps {
  columns?: number | 'equal';
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  centered?: boolean;
  stretched?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function GridRow({
  columns,
  textAlign,
  verticalAlign,
  centered,
  stretched,
  className,
  style,
  children,
}: GridRowProps) {
  return (
    <div
      className={cx(
        columns === 'equal' && 'equal width',
        typeof columns === 'number' && `${numberToWord(columns)} column`,
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        centered && 'centered',
        stretched && 'stretched',
        'row',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Grid.Row = GridRow;

interface GridColumnProps {
  width?: number;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  computer?: number;
  tablet?: number;
  mobile?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function GridColumn({
  width,
  textAlign,
  verticalAlign,
  computer,
  tablet,
  mobile,
  className,
  style,
  children,
}: GridColumnProps) {
  return (
    <div
      className={cx(
        width != null && `${numberToWord(width)} wide`,
        textAlign && `${textAlign} aligned`,
        verticalAlign && `${verticalAlign} aligned`,
        computer != null && `${numberToWord(computer)} wide computer`,
        tablet != null && `${numberToWord(tablet)} wide tablet`,
        mobile != null && `${numberToWord(mobile)} wide mobile`,
        'column',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Grid.Column = GridColumn;

export { Grid };

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

export interface BreadcrumbProps {
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

interface BreadcrumbComponent extends React.FC<BreadcrumbProps> {
  Section: typeof BreadcrumbSection;
  Divider: typeof BreadcrumbDivider;
}

function BreadcrumbBase({ size, className, style, children }: BreadcrumbProps) {
  return (
    <div
      className={cx('ui', size, 'breadcrumb', className)}
      style={style}
      aria-label="breadcrumb"
    >
      {children}
    </div>
  );
}

const Breadcrumb = BreadcrumbBase as BreadcrumbComponent;

interface BreadcrumbSectionProps {
  active?: boolean;
  link?: boolean;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  className?: string;
  children?: ReactNode;
}
function BreadcrumbSection({ active, link, href, onClick, className, children }: BreadcrumbSectionProps) {
  if (link || href || onClick) {
    return (
      <a
        href={href ?? '#'}
        onClick={onClick}
        className={cx('section', active && 'active', className)}
      >
        {children}
      </a>
    );
  }
  return <div className={cx('section', active && 'active', className)}>{children}</div>;
}
Breadcrumb.Section = BreadcrumbSection;

function BreadcrumbDivider({
  icon,
  className,
  children,
}: {
  icon?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span className={cx('divider', className)}>
      {icon ? <Icon name={icon} /> : children ?? '/'}
    </span>
  );
}
Breadcrumb.Divider = BreadcrumbDivider;

export { Breadcrumb };

// ---------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------

export interface ImageProps {
  src?: string;
  alt?: string;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  shape?: 'circular' | 'rounded';
  circular?: boolean;
  rounded?: boolean;
  centered?: boolean;
  inline?: boolean;
  spaced?: boolean | 'left' | 'right';
  avatar?: boolean;
  bordered?: boolean;
  fluid?: boolean;
  floated?: 'left' | 'right';
  hidden?: boolean;
  className?: string;
  style?: CSSProperties;
  onClick?: (event: MouseEvent<HTMLImageElement>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement>) => void;
  width?: string | number;
  height?: string | number;
}

export function Image({
  src,
  alt,
  size,
  shape,
  circular,
  rounded,
  centered,
  inline,
  spaced,
  avatar,
  bordered,
  fluid,
  floated,
  hidden,
  className,
  style,
  onClick,
  onError,
  width,
  height,
  ...rest
}: ImageProps) {
  return (
    <img
      src={src}
      alt={alt as string}
      width={width}
      height={height}
      onClick={onClick}
      onError={onError}
      className={cx(
        'ui',
        size,
        shape,
        circular && 'circular',
        rounded && 'rounded',
        avatar && 'avatar',
        bordered && 'bordered',
        fluid && 'fluid',
        centered && 'centered',
        inline && 'inline',
        floated && `${floated} floated`,
        hidden && 'hidden',
        spaced === true && 'spaced',
        typeof spaced === 'string' && `${spaced} spaced`,
        'image',
        className,
      )}
      style={style}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// Divider
// ---------------------------------------------------------------------------

export interface DividerProps {
  hidden?: boolean;
  horizontal?: boolean;
  section?: boolean;
  clearing?: boolean;
  fitted?: boolean;
  inverted?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
export function Divider({
  hidden,
  horizontal,
  section,
  clearing,
  fitted,
  inverted,
  className,
  style,
  children,
}: DividerProps) {
  return (
    <div
      role="separator"
      className={cx(
        'ui',
        hidden && 'hidden',
        horizontal && 'horizontal',
        section && 'section',
        clearing && 'clearing',
        fitted && 'fitted',
        inverted && 'inverted',
        'divider',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checkbox (top-level)
// ---------------------------------------------------------------------------

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  name?: string;
  value?: string | number;
  toggle?: boolean;
  slider?: boolean;
  radio?: boolean;
  indeterminate?: boolean;
  className?: string;
  style?: CSSProperties;
  onChange?: (
    event: React.FormEvent<HTMLInputElement>,
    data: { checked: boolean; name?: string; value?: string | number },
  ) => void;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Checkbox({
  checked,
  defaultChecked,
  disabled,
  label,
  name,
  value,
  toggle,
  slider,
  radio,
  indeterminate,
  className,
  style,
  onChange,
  onClick,
  ...rest
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);
  return (
    <div
      className={cx(
        'ui',
        toggle && 'toggle',
        slider && 'slider',
        radio && 'radio',
        checked && 'checked',
        disabled && 'disabled',
        'checkbox',
        className,
      )}
      style={style}
      onClick={onClick}
      {...rest}
    >
      <input
        ref={inputRef}
        type={radio ? 'radio' : 'checkbox'}
        name={name}
        value={value}
        defaultChecked={defaultChecked}
        checked={checked ?? undefined}
        disabled={disabled}
        onChange={(e) => onChange?.(e, { checked: e.target.checked, name, value })}
      />
      {label != null ? <label>{label}</label> : <label />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm (small dialog wrapper around Modal)
// ---------------------------------------------------------------------------

export interface ConfirmProps {
  open?: boolean;
  header?: ReactNode;
  content?: ReactNode;
  cancelButton?: ReactNode;
  confirmButton?: ReactNode;
  size?: ModalProps['size'];
  onCancel?: (event: AnyEvent) => void;
  onConfirm?: (event: AnyEvent) => void;
}

export function Confirm({
  open,
  header,
  content,
  cancelButton = 'Cancel',
  confirmButton = 'OK',
  size = 'small',
  onCancel,
  onConfirm,
}: ConfirmProps) {
  return (
    <Modal open={open} onClose={onCancel} size={size}>
      {header ? <Modal.Header>{header}</Modal.Header> : null}
      {content ? <Modal.Content>{content}</Modal.Content> : null}
      <Modal.Actions>
        <Button onClick={onCancel} type="button">
          {cancelButton}
        </Button>
        <Button primary onClick={onConfirm} type="button">
          {confirmButton}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface ListProps {
  bulleted?: boolean;
  ordered?: boolean;
  divided?: boolean;
  relaxed?: boolean | 'very';
  selection?: boolean;
  celled?: boolean;
  horizontal?: boolean;
  inverted?: boolean;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  link?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

interface ListComponent extends React.FC<ListProps> {
  Item: typeof ListItem;
  Content: typeof ListContent;
  Header: typeof ListHeader;
  Description: typeof ListDescription;
  Icon: typeof ListIcon;
  List: typeof ListInner;
}

function ListBase({
  bulleted,
  ordered,
  divided,
  relaxed,
  selection,
  celled,
  horizontal,
  inverted,
  size,
  link,
  className,
  style,
  children,
}: ListProps) {
  return (
    <div
      role="list"
      className={cx(
        'ui',
        size,
        bulleted && 'bulleted',
        ordered && 'ordered',
        divided && 'divided',
        relaxed === true && 'relaxed',
        relaxed === 'very' && 'very relaxed',
        selection && 'selection',
        celled && 'celled',
        horizontal && 'horizontal',
        inverted && 'inverted',
        link && 'link',
        'list',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

const List = ListBase as ListComponent;

interface ListItemProps {
  active?: boolean;
  disabled?: boolean;
  icon?: string | ReactNode;
  description?: ReactNode;
  header?: ReactNode;
  content?: ReactNode;
  value?: string | number;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function ListItem({
  active,
  disabled,
  icon,
  description,
  header,
  content,
  value,
  onClick,
  className,
  style,
  children,
}: ListItemProps) {
  const iconNode = typeof icon === 'string' ? <Icon name={icon} /> : icon;
  return (
    <div
      role="listitem"
      data-value={value}
      onClick={onClick}
      className={cx(active && 'active', disabled && 'disabled', 'item', className)}
      style={style}
    >
      {iconNode}
      {(header || description || content) ? (
        <div className="content">
          {header ? <div className="header">{header}</div> : null}
          {description ? <div className="description">{description}</div> : null}
          {content}
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
List.Item = ListItem;

function ListContent({
  className,
  children,
  floated,
}: {
  className?: string;
  floated?: 'left' | 'right';
  children?: ReactNode;
}) {
  return (
    <div className={cx(floated && `${floated} floated`, 'content', className)}>{children}</div>
  );
}
List.Content = ListContent;

function ListHeader({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('header', className)}>{children}</div>;
}
List.Header = ListHeader;

function ListDescription({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('description', className)}>{children}</div>;
}
List.Description = ListDescription;

function ListIcon({
  name,
  className,
  ...rest
}: IconProps) {
  return <Icon name={name} className={className} {...rest} />;
}
List.Icon = ListIcon;

function ListInner({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('list', className)}>{children}</div>;
}
List.List = ListInner;

export { List };

// ---------------------------------------------------------------------------
// Popup (very simplified: hover/click tooltip)
// ---------------------------------------------------------------------------

export interface PopupProps {
  trigger?: ReactElement;
  content?: ReactNode;
  position?:
    | 'top left'
    | 'top right'
    | 'bottom left'
    | 'bottom right'
    | 'right center'
    | 'left center'
    | 'top center'
    | 'bottom center';
  on?: 'hover' | 'click' | 'focus' | Array<'hover' | 'click' | 'focus'>;
  inverted?: boolean;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large' | 'big' | 'huge' | 'massive';
  open?: boolean;
  defaultOpen?: boolean;
  className?: string;
  style?: CSSProperties;
  header?: ReactNode;
  children?: ReactNode;
  hoverable?: boolean;
  hideOnScroll?: boolean;
  pinned?: boolean;
  basic?: boolean;
  wide?: boolean | 'very';
  flowing?: boolean;
}

export function Popup({
  trigger,
  content,
  position = 'top center',
  on = 'hover',
  inverted,
  size,
  open,
  defaultOpen,
  className,
  style,
  header,
  children,
}: PopupProps) {
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(Boolean(defaultOpen));
  const isOpen = isControlled ? open : internalOpen;
  const triggers = Array.isArray(on) ? on : [on];

  const triggerProps: Record<string, (e: AnyEvent) => void> = {};
  if (triggers.includes('hover')) {
    triggerProps.onMouseEnter = () => !isControlled && setInternalOpen(true);
    triggerProps.onMouseLeave = () => !isControlled && setInternalOpen(false);
  }
  if (triggers.includes('click')) {
    triggerProps.onClick = () => !isControlled && setInternalOpen((o) => !o);
  }
  if (triggers.includes('focus')) {
    triggerProps.onFocus = () => !isControlled && setInternalOpen(true);
    triggerProps.onBlur = () => !isControlled && setInternalOpen(false);
  }

  const enhancedTrigger = trigger
    ? cloneElement(trigger, {
        ...(trigger.props as object),
        ...triggerProps,
      })
    : null;

  return (
    <>
      {enhancedTrigger}
      {isOpen ? (
        <div
          role="tooltip"
          className={cx(
            'ui',
            position,
            size,
            inverted && 'inverted',
            'popup transition visible',
            className,
          )}
          style={{
            position: 'absolute',
            zIndex: 1900,
            ...style,
          }}
        >
          {header ? <div className="header">{header}</div> : null}
          {content ? <div className="content">{content}</div> : null}
          {children}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface CardProps {
  fluid?: boolean;
  centered?: boolean;
  raised?: boolean;
  link?: boolean;
  color?: CompositeColor;
  className?: string;
  style?: CSSProperties;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  children?: ReactNode;
  header?: ReactNode;
  meta?: ReactNode;
  description?: ReactNode;
}

interface CardComponent extends React.FC<CardProps> {
  Group: typeof CardGroup;
  Content: typeof CardContent;
  Header: typeof CardHeader;
  Meta: typeof CardMeta;
  Description: typeof CardDescription;
}

function CardBase({
  fluid,
  centered,
  raised,
  link,
  color,
  className,
  style,
  onClick,
  children,
  header,
  meta,
  description,
}: CardProps) {
  const standaloneShorthand = header || meta || description;
  return (
    <div
      className={cx(
        'ui',
        color,
        fluid && 'fluid',
        centered && 'centered',
        raised && 'raised',
        link && 'link',
        'card',
        className,
      )}
      style={style}
      onClick={onClick}
    >
      {standaloneShorthand ? (
        <div className="content">
          {header ? <div className="header">{header}</div> : null}
          {meta ? <div className="meta">{meta}</div> : null}
          {description ? <div className="description">{description}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

const Card = CardBase as CardComponent;

interface CardGroupProps {
  itemsPerRow?: number;
  doubling?: boolean;
  stackable?: boolean;
  centered?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}
function CardGroup({
  itemsPerRow,
  doubling,
  stackable,
  centered,
  className,
  style,
  children,
}: CardGroupProps) {
  return (
    <div
      className={cx(
        'ui',
        itemsPerRow != null && numberToWord(itemsPerRow),
        doubling && 'doubling',
        stackable && 'stackable',
        centered && 'centered',
        'cards',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}
Card.Group = CardGroup;

interface CardContentProps {
  extra?: boolean;
  className?: string;
  children?: ReactNode;
  header?: ReactNode;
  meta?: ReactNode;
  description?: ReactNode;
}
function CardContent({ extra, className, children, header, meta, description }: CardContentProps) {
  return (
    <div className={cx(extra && 'extra', 'content', className)}>
      {header ? <div className="header">{header}</div> : null}
      {meta ? <div className="meta">{meta}</div> : null}
      {description ? <div className="description">{description}</div> : null}
      {children}
    </div>
  );
}
Card.Content = CardContent;

function CardHeader({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('header', className)}>{children}</div>;
}
Card.Header = CardHeader;

function CardMeta({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('meta', className)}>{children}</div>;
}
Card.Meta = CardMeta;

function CardDescription({ className, children }: { className?: string; children?: ReactNode }) {
  return <div className={cx('description', className)}>{children}</div>;
}
Card.Description = CardDescription;

export { Card };

// ---------------------------------------------------------------------------
// Re-exports for prop types referenced by consumers
// ---------------------------------------------------------------------------

export type { DropdownItemProps as DropdownItemPropsType };

// PaginationProps already exported above. Keep alias for parity with the
// original semantic-ui-react import shape.
